/**
 * Natures - Pokemon Nature Reference
 * Complete list of all Pokemon natures with their stat boosts/drops and berry flavor preferences
 */
import { useState, useMemo, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Search, ArrowUp, ArrowDown, Loader } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { Input } from '../components/ui/input';
import { cn } from '../lib/utils';

const POKEAPI_BASE = 'https://pokeapi.co/api/v2';

const STAT_LABELS = {
  'hp': { en: 'HP', es: 'PS' },
  'attack': { en: 'Attack', es: 'Ataque' },
  'defense': { en: 'Defense', es: 'Defensa' },
  'special-attack': { en: 'Sp. Attack', es: 'At. Especial' },
  'special-defense': { en: 'Sp. Defense', es: 'Def. Especial' },
  'speed': { en: 'Speed', es: 'Velocidad' }
};

function NatureCard({ nature, language }) {
  const getLocalizedName = (names) => {
    const langCode = language === 'es' ? 'es' : 'en';
    const localized = names?.find(n => n.language.name === langCode);
    return localized?.name || names?.[0]?.name || 'Unknown';
  };

  const statName = (stat) => {
    const name = stat?.name || '';
    return STAT_LABELS[name]?.[language] || name;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-card border border-border rounded-lg p-4 space-y-3"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-lg text-foreground capitalize">{getLocalizedName(nature.names)}</h3>
        </div>
      </div>

      {/* Stat boosts/drops */}
      <div className="flex gap-3 flex-wrap">
        {nature.increased_stat && (
          <div className="flex items-center gap-1.5 px-2 py-1 rounded bg-green-500/10 border border-green-500/30">
            <ArrowUp className="w-3.5 h-3.5 text-green-500" />
            <span className="text-xs font-semibold text-green-600 dark:text-green-400">
              +10% {statName(nature.increased_stat)}
            </span>
          </div>
        )}
        {nature.decreased_stat && (
          <div className="flex items-center gap-1.5 px-2 py-1 rounded bg-red-500/10 border border-red-500/30">
            <ArrowDown className="w-3.5 h-3.5 text-red-500" />
            <span className="text-xs font-semibold text-red-600 dark:text-red-400">
              -10% {statName(nature.decreased_stat)}
            </span>
          </div>
        )}
        {!nature.increased_stat && !nature.decreased_stat && (
          <div className="px-2 py-1 rounded bg-blue-500/10 border border-blue-500/30">
            <span className="text-xs font-semibold text-blue-600 dark:text-blue-400">
              {language === 'es' ? 'Naturaleza Neutra' : 'Neutral Nature'}
            </span>
          </div>
        )}
      </div>

      {/* Berry flavors */}
      {(nature.likes_flavor || nature.hates_flavor) && (
        <div className="pt-2 border-t border-border space-y-2">
          {nature.likes_flavor && (
            <div className="text-xs">
              <span className="text-muted-foreground">
                {language === 'es' ? 'Sabor preferido: ' : 'Likes flavor: '}
              </span>
              <span className="font-semibold text-green-600 dark:text-green-400 capitalize">
                {nature.likes_flavor?.name}
              </span>
            </div>
          )}
          {nature.hates_flavor && (
            <div className="text-xs">
              <span className="text-muted-foreground">
                {language === 'es' ? 'Sabor odiado: ' : 'Hates flavor: '}
              </span>
              <span className="font-semibold text-red-600 dark:text-red-400 capitalize">
                {nature.hates_flavor?.name}
              </span>
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
}

function Natures() {
  const { language, addToCache, getFromCache } = useApp();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all'); // all, positive, negative, neutral
  const [natures, setNatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch natures data from PokeAPI
  useEffect(() => {
    const fetchNatures = async () => {
      try {
        setLoading(true);
        setError(null);

        // Check cache first
        const cacheKey = 'natures-list';
        let naturesData = getFromCache(cacheKey);

        if (!naturesData) {
          const response = await fetch(`${POKEAPI_BASE}/nature?limit=100`);
          if (!response.ok) throw new Error('Failed to fetch natures list');
          naturesData = await response.json();
          addToCache(cacheKey, naturesData);
        }

        // Fetch details for each nature
        const detailedNatures = await Promise.all(
          naturesData.results.map(async (nature) => {
            const detailCacheKey = `nature-${nature.name}`;
            let details = getFromCache(detailCacheKey);

            if (!details) {
              const detailResponse = await fetch(nature.url);
              if (!detailResponse.ok) throw new Error(`Failed to fetch nature ${nature.name}`);
              details = await detailResponse.json();
              addToCache(detailCacheKey, details);
            }

            return details;
          })
        );

        setNatures(detailedNatures);
      } catch (err) {
        setError(err.message);
        console.error('Error fetching natures:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchNatures();
  }, [addToCache, getFromCache]);

  const getLocalizedName = (names) => {
    const langCode = language === 'es' ? 'es' : 'en';
    const localized = names?.find(n => n.language.name === langCode);
    return localized?.name || names?.[0]?.name || '';
  };

  const OFFENSIVE_STATS = ['attack', 'special-attack', 'speed'];
  const DEFENSIVE_STATS = ['defense', 'special-defense'];

  const filteredNatures = useMemo(() => {
    return natures.filter(nature => {
      const name = getLocalizedName(nature.names);
      const nameMatch = name.toLowerCase().includes(search.toLowerCase());

      if (!nameMatch) return false;

      // Filter by type: offensive = boosts atk/sp.atk/speed, defensive = boosts def/sp.def, neutral = no change
      if (filter === 'positive') return OFFENSIVE_STATS.includes(nature.increased_stat?.name);
      if (filter === 'negative') return DEFENSIVE_STATS.includes(nature.increased_stat?.name);
      if (filter === 'neutral') return !nature.increased_stat && !nature.decreased_stat;

      return true;
    });
  }, [search, filter, language, natures]); // eslint-disable-line react-hooks/exhaustive-deps

  const statCounts = useMemo(() => {
    const counts = {};
    natures.forEach(nature => {
      if (nature.increased_stat) {
        const stat = nature.increased_stat.name;
        counts[stat] = (counts[stat] || 0) + 1;
      }
    });
    return counts;
  }, [natures]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-background via-background to-background/95 pt-6 pb-12">
      <div className="container max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-4xl font-bold mb-2">
            {language === 'es' ? 'Naturalezas Pokémon' : 'Pokémon Natures'}
          </h1>
          <p className="text-muted-foreground">
            {language === 'es'
              ? 'Descubre cómo cada naturaleza afecta a los stats de tu Pokémon'
              : 'Discover how each nature affects your Pokémon\'s stats'}
          </p>
        </motion.div>

        {/* Search and Filters */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-4 mb-6"
        >
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder={language === 'es' ? 'Buscar naturaleza...' : 'Search nature...'}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Filter buttons */}
          <div className="flex gap-2 flex-wrap">
            {[
              { key: 'all', label: language === 'es' ? 'Todas' : 'All' },
              { key: 'positive', label: language === 'es' ? 'Ofensivas' : 'Offensive' },
              { key: 'negative', label: language === 'es' ? 'Defensivas' : 'Defensive' },
              { key: 'neutral', label: language === 'es' ? 'Neutrales' : 'Neutral' }
            ].map(btn => (
              <button
                key={btn.key}
                onClick={() => setFilter(btn.key)}
                className={cn(
                  'px-3 py-1.5 rounded-lg text-sm font-medium transition-all',
                  filter === btn.key
                    ? 'bg-violet-500 text-white'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                )}
              >
                {btn.label}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Loading State */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader className="w-8 h-8 animate-spin text-violet-500 mb-4" />
            <p className="text-muted-foreground">
              {language === 'es' ? 'Cargando naturalezas...' : 'Loading natures...'}
            </p>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-8 text-red-600 dark:text-red-400">
            <p>{language === 'es' ? 'Error al cargar naturalezas:' : 'Error loading natures:'} {error}</p>
          </div>
        )}

        {/* Info Cards */}
        {!loading && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-card border border-border rounded-lg p-4">
              <p className="text-sm text-muted-foreground mb-1">
                {language === 'es' ? 'Naturalezas Totales' : 'Total Natures'}
              </p>
              <p className="text-2xl font-bold">{natures.length}</p>
            </motion.div>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-card border border-border rounded-lg p-4">
              <p className="text-sm text-muted-foreground mb-1">
                {language === 'es' ? 'Natures Encontradas' : 'Natures Found'}
              </p>
              <p className="text-2xl font-bold text-violet-500">{filteredNatures.length}</p>
            </motion.div>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-card border border-border rounded-lg p-4">
              <p className="text-sm text-muted-foreground mb-1">
                {language === 'es' ? 'Natures Neutrales' : 'Neutral Natures'}
              </p>
              <p className="text-2xl font-bold">{natures.filter(n => !n.increased_stat && !n.decreased_stat).length}</p>
            </motion.div>
          </div>
        )}

        {/* Stat Distribution */}
        {!loading && Object.keys(statCounts).length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-card border border-border rounded-lg p-6 mb-8"
          >
            <h2 className="font-bold text-lg mb-4">
              {language === 'es' ? 'Distribución de Bonificaciones por Stat' : 'Stat Bonus Distribution'}
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {Object.entries(statCounts).map(([stat, count]) => (
                <div key={stat} className="bg-muted/50 rounded p-3 text-center">
                  <p className="text-xs text-muted-foreground mb-1">{STAT_LABELS[stat][language]}</p>
                  <p className="text-2xl font-bold text-violet-500">{count}</p>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Natures Grid */}
        {!loading && (
          filteredNatures.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredNatures.map((nature, idx) => (
                <div key={nature.id} style={{ animationDelay: `${idx * 50}ms` }}>
                  <NatureCard nature={nature} language={language} />
                </div>
              ))}
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-12"
            >
              <p className="text-muted-foreground">
                {language === 'es' ? 'No se encontraron naturalezas' : 'No natures found'}
              </p>
            </motion.div>
          )
        )}
      </div>
    </div>
  );
}

export default Natures;
